// src/pages/AdminAuditLogs.tsx

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';
import { getAuditLogs } from '../lib/audit/auditLogger';
import type { AuditLog } from '../types/auditLog';

const AdminAuditLogs: React.FC = () => {
  const { role, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filters
  const [userIdFilter, setUserIdFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState<AuditLog['eventType'] | ''>('');
  const [severityFilter, setSeverityFilter] = useState<AuditLog['severity'] | ''>('');
  const [resourceTypeFilter, setResourceTypeFilter] = useState<AuditLog['resourceType'] | ''>('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [limitCount, setLimitCount] = useState(100);

  useEffect(() => {
    if (!roleLoading && role !== 'admin' && role !== 'staff') {
      navigate('/client-dashboard');
    }
  }, [roleLoading, role, navigate]);

  useEffect(() => {
    if (role === 'admin' || role === 'staff') {
      loadLogs();
    }
  }, [role, userIdFilter, eventTypeFilter, severityFilter, resourceTypeFilter, startDate, endDate, limitCount]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const filters: any = {};
      if (userIdFilter) filters.userId = userIdFilter;
      if (eventTypeFilter) filters.eventType = eventTypeFilter;
      if (severityFilter) filters.severity = severityFilter;
      if (resourceTypeFilter) filters.resourceType = resourceTypeFilter;
      if (startDate) filters.startDate = new Date(startDate);
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999); // End of day
        filters.endDate = end;
      }
      if (limitCount) filters.limitCount = limitCount;

      const auditLogs = await getAuditLogs(filters);
      setLogs(auditLogs);
    } catch (err: any) {
      console.error('Error loading audit logs:', err);
      setError('Failed to load audit logs: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: any): string => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);
    } catch {
      return 'Invalid date';
    }
  };

  const getSeverityColor = (severity: AuditLog['severity']): string => {
    switch (severity) {
      case 'critical': return '#dc3545';
      case 'error': return '#fd7e14';
      case 'warning': return '#ffc107';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const exportLogs = () => {
    const csv = [
      ['Timestamp', 'User Email', 'Role', 'Event Type', 'Severity', 'Resource Type', 'Resource ID', 'Description', 'Success', 'IP Address'].join(','),
      ...logs.map(log => [
        formatTimestamp(log.timestamp),
        log.userEmail,
        log.userRole,
        log.eventType,
        log.severity,
        log.resourceType,
        log.resourceId || '',
        `"${log.description.replace(/"/g, '""')}"`,
        log.success ? 'Yes' : 'No',
        log.ipAddress || ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (roleLoading || loading) {
    return (
      <div>
        <Navigation />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <div>Loading...</div>
        </div>
      </div>
    );
  }

  if (role !== 'admin' && role !== 'staff') {
    return null; // Will redirect via useEffect
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1600px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ margin: 0 }}>Audit Logs</h1>
            <p style={{ color: '#666', marginTop: '0.5rem' }}>
              View and search system audit logs for compliance and security monitoring
            </p>
          </div>
          <button
            onClick={exportLogs}
            disabled={logs.length === 0}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: logs.length === 0 ? '#6c757d' : '#28a745',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: logs.length === 0 ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: '500'
            }}
          >
            Export CSV
          </button>
        </div>

        {error && (
          <div style={{
            padding: '1rem',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            marginBottom: '1.5rem',
          }}>
            {error}
          </div>
        )}

        {/* Filters */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          padding: '1.5rem',
          marginBottom: '1.5rem',
        }}>
          <h3 style={{ marginTop: 0, marginBottom: '1rem', fontSize: '1.125rem' }}>Filters</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                User ID
              </label>
              <input
                type="text"
                value={userIdFilter}
                onChange={(e) => setUserIdFilter(e.target.value)}
                placeholder="Filter by user ID"
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Event Type
              </label>
              <select
                value={eventTypeFilter}
                onChange={(e) => setEventTypeFilter(e.target.value as AuditLog['eventType'] | '')}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              >
                <option value="">All Events</option>
                <option value="user_login">User Login</option>
                <option value="user_logout">User Logout</option>
                <option value="user_signup">User Signup</option>
                <option value="failed_login">Failed Login</option>
                <option value="data_viewed">Data Viewed</option>
                <option value="sensitive_data_accessed">Sensitive Data Accessed</option>
                <option value="data_created">Data Created</option>
                <option value="data_updated">Data Updated</option>
                <option value="data_deleted">Data Deleted</option>
                <option value="task_created">Task Created</option>
                <option value="task_updated">Task Updated</option>
                <option value="task_deleted">Task Deleted</option>
                <option value="user_created">User Created</option>
                <option value="user_updated">User Updated</option>
                <option value="user_deleted">User Deleted</option>
                <option value="role_changed">Role Changed</option>
                <option value="privacy_request_submitted">Privacy Request Submitted</option>
                <option value="entity_formation_submitted">Entity Formation Submitted</option>
                <option value="permission_denied">Permission Denied</option>
                <option value="unauthorized_access_attempt">Unauthorized Access Attempt</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Severity
              </label>
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value as AuditLog['severity'] | '')}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              >
                <option value="">All Severities</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="error">Error</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Resource Type
              </label>
              <select
                value={resourceTypeFilter}
                onChange={(e) => setResourceTypeFilter(e.target.value as AuditLog['resourceType'] | '')}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              >
                <option value="">All Resources</option>
                <option value="user">User</option>
                <option value="task">Task</option>
                <option value="document">Document</option>
                <option value="privacy_request">Privacy Request</option>
                <option value="entity_formation_submission">Entity Formation</option>
                <option value="system">System</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500' }}>
                Limit
              </label>
              <input
                type="number"
                value={limitCount}
                onChange={(e) => setLimitCount(parseInt(e.target.value) || 100)}
                min={1}
                max={10000}
                style={{ width: '100%', padding: '0.5rem', border: '1px solid #ced4da', borderRadius: '4px' }}
              />
            </div>
          </div>
        </div>

        {/* Logs Table */}
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          border: '1px solid #dee2e6',
          overflow: 'hidden',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          {logs.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#666' }}>
              No audit logs found matching the current filters.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Timestamp</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>User</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Event</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Severity</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Resource</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Description</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>Status</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontWeight: '600', fontSize: '0.875rem' }}>IP Address</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr
                      key={log.id}
                      style={{
                        borderBottom: '1px solid #dee2e6',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
                        {formatTimestamp(log.timestamp)}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                        <div>{log.userEmail}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{log.userRole}</div>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                        {log.eventType.replace(/_/g, ' ')}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <span
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: getSeverityColor(log.severity),
                            color: 'white',
                          }}
                        >
                          {log.severity}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>
                        <div>{log.resourceType}</div>
                        {log.resourceId && (
                          <div style={{ fontSize: '0.75rem', color: '#6c757d' }}>{log.resourceId.substring(0, 8)}...</div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', maxWidth: '300px' }}>
                        {log.description}
                      </td>
                      <td style={{ padding: '0.75rem' }}>
                        <span
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            fontWeight: '500',
                            backgroundColor: log.success ? '#d4edda' : '#f8d7da',
                            color: log.success ? '#155724' : '#721c24',
                          }}
                        >
                          {log.success ? 'Success' : 'Failed'}
                        </span>
                        {log.errorMessage && (
                          <div style={{ fontSize: '0.75rem', color: '#dc3545', marginTop: '0.25rem' }}>
                            {log.errorMessage}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem', fontSize: '0.875rem', fontFamily: 'monospace' }}>
                        {log.ipAddress || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {logs.length > 0 && (
          <div style={{ marginTop: '1rem', textAlign: 'center', color: '#666', fontSize: '0.875rem' }}>
            Showing {logs.length} log{logs.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditLogs;
